import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Expedition } from '../types';
import { Language } from '../translations';
import { getLocalizedPath, formatDuration } from '../lib/i18n';

declare global {
    interface Window {
        d3: any;
    }
}

const provinceColors: Record<string, string> = {
    'Provincia Coyhaique': '#00d4ff',
    'Provincia Aysén': '#0f766e',
    'Provincia General Carrera': '#0ea5e9',
    'Provincia Capitán Prat': '#3b82f6',
    'Provincia Última Esperanza': '#0284c7',
    'Desconocida': '#334155'
};

const categoryColors: Record<string, string> = {
    'trekking': '#22c55e',
    'climbing': '#f59e0b',
    'water': '#0ea5e9',
    'wellness': '#ec4899'
};

const tooltipStyle: React.CSSProperties = {
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    color: '#fff',
    padding: '12px 14px',
    borderRadius: '4px',
    pointerEvents: 'none',
    fontSize: '11px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    opacity: 0,
    transition: 'opacity 0.2s',
    zIndex: 150,
    border: '1px solid #00d4ff',
    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.8)',
    minWidth: '180px',
    maxWidth: '240px',
    backdropFilter: 'blur(4px)'
};

const getProvinceName = (comunaName: string) => {
    if (!comunaName) return 'Desconocida';
    const name = comunaName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z]/g, "");

    if (name.includes('coyhaique') || name.includes('lagoverde') || name.includes('coihaique')) return 'Provincia Coyhaique';
    if (name.includes('aysen') || name.includes('aisen') || name.includes('cisnes') || name.includes('guaitecas')) return 'Provincia Aysén';
    if (name.includes('chilechico') || name.includes('ibanez')) return 'Provincia General Carrera';
    if (name.includes('cochrane') || name.includes('higgins') || name.includes('tortel')) return 'Provincia Capitán Prat';
    if (name.includes('natales') || name.includes('paine') || name.includes('esperanza')) return 'Provincia Última Esperanza';

    return 'Desconocida';
};

const EXCURSION_COORDINATES: Record<string, [number, number]> = {
    'laguna': [-72.164, -46.121],
    'horquetas': [-72.062, -46.064],
    'marmol': [-72.674, -46.634],
    'wellness': [-72.152, -46.132],
    'rock-climbing': [-72.064, -45.571],
    'trad-climbing-cerro-colorado': [-71.722, -46.541],
    'ice-climbing-portezuelo': [-72.151, -46.082],
    'torres-del-avellano': [-72.671, -46.462],
    'ice-trekking-exploradores': [-73.192, -46.494],
    'fly-fishing': [-72.051, -45.582],
    'kayak-tranquilo': [-72.650, -46.620],
    'torres-del-paine-w': [-72.973, -50.941],
    'laguna-yagan': [-72.180, -46.105],
    'escalada-en-roca-cerro-castillo': [-72.160, -46.125],
};

interface AysenMapProps {
    expeditions?: Expedition[];
    lang?: Language;
    t?: any;
    getExpField?: <T>(exp: any, field: string, lang: Language) => T;
}

const AysenMap: React.FC<AysenMapProps> = ({ expeditions = [], lang = 'es', t = {}, getExpField }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const navigate = useNavigate();

    const allExcursions = useMemo(() => {
        return (expeditions || [])
            .filter((ex: any) => ex.is_visible !== false && EXCURSION_COORDINATES[ex.id])
            .map((ex: any) => ({
                ...ex,
                coords: EXCURSION_COORDINATES[ex.id]
            }));
    }, [expeditions]);

    const filteredExcursions = useMemo(() => {
        if (!selectedCategory) return allExcursions;
        return allExcursions.filter((ex: any) => ex.category === selectedCategory);
    }, [allExcursions, selectedCategory]);

    // Bi-directional highlighting and auto-scroll logic
    useEffect(() => {
        const d3 = window.d3;
        if (!d3 || !svgRef.current) return;
        
        const svg = d3.select(svgRef.current);
        
        // Reset all pins styles
        svg.selectAll('.pin circle')
            .transition()
            .duration(200)
            .attr('r', 5)
            .attr('stroke', '#ffffff')
            .attr('stroke-width', 2);

        // Highlight selected pin and scroll list
        if (hoveredId) {
            svg.select(`#pin-${hoveredId} circle`)
                .transition()
                .duration(200)
                .attr('r', 10)
                .attr('stroke', '#00d4ff')
                .attr('stroke-width', 4);

            // Auto-scroll the list to the hovered item
            const listItem = document.getElementById(`list-item-${hoveredId}`);
            if (listItem) {
                listItem.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }
        }
    }, [hoveredId]);

    useEffect(() => {
        let scriptLoadTimeout: NodeJS.Timeout;

        const buildTooltipHtml = (d: any) => `
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px">
                <div style="width:8px;height:8px;border-radius:50%;background-color:${categoryColors[d.category] || '#fff'}"></div>
                <div style="font-size:9px;color:#9ca3af;font-weight:700;letter-spacing:0.15em">${d.category.toUpperCase()}</div>
            </div>
            <div style="color:#fff;font-size:13px;font-weight:700;margin-bottom:4px;text-transform:none;letter-spacing:0">${d.name_es}</div>
            <div style="font-size:9px;color:#00d4ff;font-weight:700">⏱ ${d.duration}</div>
        `;

        const initMap = async () => {
            const d3 = window.d3;
            if (!d3 || !svgRef.current || !containerRef.current) return;

            try {
                let geojsonData: any;
                try {
                    geojsonData = await d3.json('/data/patagonia.geojson');
                } catch {
                    // Resilient fallback if local file is missing
                    const [geo11, geo12] = await Promise.all([
                        d3.json('https://raw.githubusercontent.com/caracena/chile-geojson/master/11.geojson'),
                        d3.json('https://raw.githubusercontent.com/caracena/chile-geojson/master/12.geojson')
                    ]);
                    geojsonData = {
                        type: 'FeatureCollection',
                        features: [...(geo11?.features || []), ...(geo12?.features || [])]
                    };
                }

                if (!geojsonData || !geojsonData.features) {
                    throw new Error("Invalid GeoJSON data format");
                }

                // Sanitize any inverted polygon winding order (prevents D3 from treating an inverted ring as covering the entire globe)
                geojsonData.features.forEach((f: any) => {
                    const geom = f.geometry;
                    if (!geom) return;
                    if (geom.type === 'Polygon') {
                        if (d3.geoArea(geom) > 2 * Math.PI) {
                            geom.coordinates[0].reverse();
                        }
                    } else if (geom.type === 'MultiPolygon') {
                        geom.coordinates.forEach((poly: any) => {
                            if (d3.geoArea({ type: 'Polygon', coordinates: poly }) > 2 * Math.PI) {
                                poly[0].reverse();
                            }
                        });
                    }
                });

                // Filter out "Desconocida" regions (keeping active provinces: Aysén & Última Esperanza)
                const activeFeatures = geojsonData.features.filter((d: any) => {
                    const comunaName = d.properties?.Comuna || d.properties?.comuna || d.properties?.NOM_COM || '';
                    return getProvinceName(comunaName) !== 'Desconocida';
                });

                const filteredGeojsonData = {
                    type: 'FeatureCollection',
                    features: activeFeatures
                };

                const width = containerRef.current.clientWidth;
                const isMobile = width < 768;
                const height = isMobile ? 650 : 500;
                const padding = isMobile ? 4 : 20;

                const svg = d3.select(svgRef.current);
                svg.selectAll('*').remove();
                // Explicitly set SVG dimensions to match D3's height variable
                svg.attr('width', width).attr('height', height);

                let projection;
                if (isMobile) {
                    // On mobile use the full region, fitted to the tall 650px container.
                    // Aysén is geographically tall and narrow — the map will fill the height
                    // showing the complete region from northern Aysén to Torres del Paine.
                    projection = d3.geoMercator()
                        .fitExtent([[padding, padding], [width - padding, height - padding]], filteredGeojsonData);
                } else {
                    // Desktop: fit to the full active region
                    projection = d3.geoMercator()
                        .fitExtent([[padding, padding], [width - padding, height - padding]], filteredGeojsonData);
                }

                const pathGenerator = d3.geoPath().projection(projection);
                const tooltip = d3.select(containerRef.current).select('.map-tooltip');

                const g = svg.append('g');

                // Map standard features (Comunas)
                g.append('g')
                    .selectAll('path')
                    .data(activeFeatures)
                    .enter()
                    .append('path')
                    .attr('d', pathGenerator)
                    .attr('fill', (d: any) => {
                        const comunaName = d.properties?.Comuna || d.properties?.comuna || d.properties?.NOM_COM || '';
                        const province = getProvinceName(comunaName);
                        return provinceColors[province] || provinceColors['Desconocida'];
                    })
                    .attr('stroke', '#ffffff')
                    .attr('stroke-width', 0.5)
                    .attr('class', 'transition-all duration-300')
                    .attr('opacity', 0.8)
                    .on('mouseover', function (this: any, event: any, d: any) {
                        d3.select(this)
                            .attr('stroke-width', 1.5)
                            .attr('opacity', 1);

                        const comunaName = d.properties?.Comuna || d.properties?.comuna || d.properties?.NOM_COM || 'Desconocida';
                        const province = getProvinceName(comunaName);

                        tooltip.style('opacity', 1)
                            .html(`<div class="text-[#00d4ff] mb-1 font-title text-sm leading-tight uppercase tracking-wider">${comunaName}</div>
                      <div class="text-[10px] text-gray-400 font-light">📍 ${province}</div>`);
                    })
                    .on('mousemove', function (this: any, event: any) {
                        const [x, y] = d3.pointer(event, containerRef.current);
                        const containerW = containerRef.current?.clientWidth || 400;
                        const tipW = 200;
                        const clampedX = Math.min(Math.max(x, tipW / 2 + 8), containerW - tipW / 2 - 8);
                        const clampedY = y > 60 ? y - 28 : y + 36;
                        tooltip
                            .style('left', `${clampedX}px`)
                            .style('top', `${clampedY}px`)
                            .style('transform', y > 60 ? 'translate(-50%, -100%)' : 'translate(-50%, 0)');
                    })
                    .on('mouseout', function (this: any, event: any, d: any) {
                        d3.select(this)
                            .attr('stroke-width', 0.5)
                            .attr('opacity', 0.8);
                        tooltip.style('opacity', 0);
                    });

                const pinsG = g.append('g').attr('class', 'pins-layer');

                const pins = pinsG.selectAll('.pin')
                    .data(filteredExcursions)
                    .enter()
                    .append('g')
                    .attr('class', 'pin cursor-pointer')
                    .attr('id', (d: any) => `pin-${d.id}`)
                    .attr('transform', (d: any) => {
                        const [x, y] = projection(d.coords);
                        return `translate(${x},${y})`;
                    })
                    .on('mouseover', function (this: any, event: any, d: any) {
                        // Desktop only: ephemeral hover highlight
                        setHoveredId(d.id);
                        const tooltipHtml = buildTooltipHtml(d);
                        const containerW = containerRef.current?.clientWidth || 400;
                        const [x, y] = d3.pointer(event, containerRef.current);
                        const tipW = 200;
                        const clampedX = Math.min(Math.max(x, tipW / 2 + 8), containerW - tipW / 2 - 8);
                        const clampedY = y > 60 ? y - 28 : y + 36;
                        tooltip.style('opacity', 1)
                            .style('left', `${clampedX}px`)
                            .style('top', `${clampedY}px`)
                            .style('transform', y > 60 ? 'translate(-50%, -100%)' : 'translate(-50%, 0)')
                            .html(tooltipHtml);
                    })
                    .on('touchstart', function (this: any, event: any, d: any) {
                        // Mobile: persistent selection — pin stays highlighted after tap
                        event.preventDefault();
                        setHoveredId(d.id);
                        const tooltipHtml = buildTooltipHtml(d);
                        const containerW = containerRef.current?.clientWidth || 400;
                        const touch = event.touches?.[0] || event;
                        const [x, y] = d3.pointer(touch, containerRef.current);
                        const tipW = 200;
                        const clampedX = Math.min(Math.max(x, tipW / 2 + 8), containerW - tipW / 2 - 8);
                        const clampedY = y > 60 ? y - 28 : y + 36;
                        tooltip.style('opacity', 1)
                            .style('left', `${clampedX}px`)
                            .style('top', `${clampedY}px`)
                            .style('transform', y > 60 ? 'translate(-50%, -100%)' : 'translate(-50%, 0)')
                            .html(tooltipHtml);
                        // Auto-hide tooltip after 3s on mobile (pin stays highlighted)
                        setTimeout(() => tooltip.style('opacity', 0), 3000);
                    })
                    .on('mousemove', function (this: any, event: any) {
                        const [x, y] = d3.pointer(event, containerRef.current);
                        const containerW = containerRef.current?.clientWidth || 400;
                        const tipW = 200;
                        const clampedX = Math.min(Math.max(x, tipW / 2 + 8), containerW - tipW / 2 - 8);
                        const clampedY = y > 60 ? y - 28 : y + 36;
                        tooltip
                            .style('left', `${clampedX}px`)
                            .style('top', `${clampedY}px`)
                            .style('transform', y > 60 ? 'translate(-50%, -100%)' : 'translate(-50%, 0)');
                    })
                    .on('mouseout', function (this: any) {
                        // Desktop only: clear hover on mouse leave
                        setHoveredId(null);
                        tooltip.style('opacity', 0);
                    })
                    // touchend: intentionally does NOT clear hoveredId on mobile
                    .on('click', (event: any, d: any) => {
                        navigate(getLocalizedPath(`/expeditions/${d.slug}`, lang));
                    });

                // Tap on SVG background (not a pin) clears mobile selection
                svg.on('touchstart', function(event: any) {
                    if (!(event.target as Element).closest('.pin')) {
                        setHoveredId(null);
                        tooltip.style('opacity', 0);
                    }
                });

                // Pin circle with Pulse effect
                // Invisible larger touch target
                pins.append('circle')
                    .attr('r', 14)
                    .attr('fill', 'transparent')
                    .attr('stroke', 'none');

                pins.append('circle')
                    .attr('class', 'pin-main-circle')
                    .attr('r', isMobile ? 7 : 5)
                    .attr('fill', (d: any) => categoryColors[d.category] || '#00d4ff')
                    .attr('stroke', '#ffffff')
                    .attr('stroke-width', 2)
                    .attr('filter', 'drop-shadow(0 0 4px rgba(0,0,0,0.5))');

                const pulse = () => {
                    svg.selectAll('.pin-main-circle')
                        .filter(function(d: any) { 
                            // Only pulse if NOT hovered
                            const isSelected = document.getElementById(`list-item-${d.id}`)?.classList.contains('bg-[#1a1a1a]');
                            return !isSelected;
                        })
                        .transition()
                        .duration(1500)
                        .attr('r', 7)
                        .transition()
                        .duration(1500)
                        .attr('r', 5)
                        .on('end', pulse);
                };
                pulse();

                svg.selectAll('.pin').raise();
                setLoading(false);

            } catch (err: any) {
                setLoading(false);
                setError(err.message || "Error loading map data");
            }
        };

        if (window.d3) {
            initMap();
        } else {
            const scriptId = 'd3-cdn-script';
            let script = document.getElementById(scriptId) as HTMLScriptElement;

            if (!script) {
                script = document.createElement('script');
                script.id = scriptId;
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/d3/7.9.0/d3.min.js';
                script.async = true;
                document.body.appendChild(script);
            }

            const onScriptLoad = () => {
                clearTimeout(scriptLoadTimeout);
                initMap();
            };

            script.addEventListener('load', onScriptLoad);

            scriptLoadTimeout = setTimeout(() => {
                if (!window.d3) {
                    setError("Timeout loading D3 from CDN");
                    setLoading(false);
                }
            }, 10000);

            return () => {
                script.removeEventListener('load', onScriptLoad);
                clearTimeout(scriptLoadTimeout);
            };
        }

        const handleResize = () => {
            if (window.d3 && !loading && !error) {
                initMap();
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);

    }, [navigate, filteredExcursions]);

    return (
        <div className="bg-stone py-24 px-6 border-y border-rock">
            <div className="max-w-7xl mx-auto flex flex-col items-center">
                <span className="text-[10px] uppercase font-bold tracking-[0.5em] text-[#00d4ff] mb-4">
                    {t.map_badge || (lang === 'en' ? 'Location & Coverage' : 'Ubicación y Cobertura')}
                </span>
                <h2 className="text-4xl md:text-5xl font-title uppercase tracking-tighter text-white mb-4 text-center">
                    {t.map_title || (lang === 'en' ? 'Our Routes in Aysén' : 'Nuestras Rutas en Aysén')}
                </h2>
                <p className="text-gray-400 max-w-2xl mb-16 text-sm uppercase tracking-widest font-light text-center px-2">
                    {t.map_desc || (lang === 'en' 
                        ? 'Explore our strategic operating locations across the Aysén Region and Patagonia. Select a category to filter.' 
                        : 'Explora los puntos estratégicos donde operamos desde la Región de Aysén hasta las Torres del Paine. Selecciona una categoría para filtrar.')}
                </p>

                <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 w-full items-start">
                    {/* Left: Map */}
                    <div ref={containerRef} className="relative w-full h-[650px] lg:flex-1 lg:h-[500px]">
                        {loading && !error && (
                            <div className="absolute inset-0 flex items-center justify-center text-[#00d4ff] uppercase text-xs font-bold tracking-widest animate-pulse">
                                {lang === 'en' ? 'Loading Map...' : 'Cargando Mapa...'}
                            </div>
                        )}
                        {error && (
                            <div className="absolute inset-0 flex items-center justify-center text-red-500 uppercase text-xs font-bold tracking-widest text-center">
                                {error}
                            </div>
                        )}
                        <svg className="w-full h-full cursor-default" ref={svgRef}></svg>
                        <div className="map-tooltip" style={tooltipStyle}></div>
                    </div>

                    {/* Right: List */}
                    <div className="w-full lg:w-96 flex flex-col gap-2.5 lg:max-h-[500px] lg:overflow-y-auto lg:pr-4 custom-scrollbar">
                        <div className="flex justify-between items-center mb-2 border-b border-rock pb-2">
                            <div className="text-[10px] uppercase font-bold tracking-widest text-gray-500">
                                {selectedCategory ? `${selectedCategory} (${filteredExcursions.length})` : (t.map_directory || (lang === 'en' ? 'Full Catalog' : 'Directorio Completo'))}
                            </div>
                            {selectedCategory && (
                                <button
                                    onClick={() => setSelectedCategory(null)}
                                    className="text-[10px] text-[#00d4ff] font-bold uppercase hover:underline"
                                >
                                    {t.map_view_all || (lang === 'en' ? 'View all' : 'Ver todas')}
                                </button>
                            )}
                        </div>
                        {filteredExcursions.length > 0 ? (
                            filteredExcursions.map((ex: any) => {
                                const expName = getExpField ? getExpField(ex, 'name', lang) : (lang === 'en' ? (ex.name_en || ex.name_es) : ex.name_es);
                                const localizedDuration = formatDuration(ex.duration, lang);
                                return (
                                    <div
                                        key={ex.id}
                                        id={`list-item-${ex.id}`}
                                        className={`group cursor-pointer p-4 border transition-all duration-300 rounded-lg ${
                                            hoveredId === ex.id
                                                ? 'bg-[#1a1a1a] border-[#00d4ff] translate-x-1'
                                                : 'bg-[#0a0a0a]/50 border-rock hover:border-gray-600'
                                        }`}
                                        onMouseEnter={() => setHoveredId(ex.id)}
                                        onMouseLeave={() => setHoveredId(null)}
                                        onClick={() => navigate(getLocalizedPath(`/expeditions/${ex.slug}`, lang))}
                                    >
                                        <div className="flex items-center gap-3 mb-1">
                                            <div
                                                className="w-2 h-2 rounded-full"
                                                style={{ backgroundColor: categoryColors[ex.category] || '#fff' }}
                                            ></div>
                                            <div className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">
                                                {ex.category}
                                            </div>
                                        </div>
                                        <h3 className={`text-sm font-title uppercase tracking-wide transition-colors ${
                                            hoveredId === ex.id ? 'text-[#00d4ff]' : 'text-white group-hover:text-[#00d4ff]'
                                        }`}>
                                            {expName}
                                        </h3>
                                        <div className="flex justify-between items-center mt-2">
                                            <span className="text-[10px] text-gray-500 uppercase">{localizedDuration}</span>
                                            <span className={`text-[10px] uppercase font-bold tracking-widest transition-opacity ${
                                                hoveredId === ex.id ? 'opacity-100 text-[#00d4ff]' : 'opacity-0'
                                            }`}>
                                                {t.map_view_more || (lang === 'en' ? 'View more →' : 'Ver más →')}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="p-12 text-center text-gray-600 uppercase text-[10px] font-bold tracking-tighter">
                                {t.map_no_results || (lang === 'en' ? 'No results found' : 'Sin resultados')}
                            </div>
                        )}
                    </div>
                </div>

                {/* Filter Legend */}
                <div className="mt-16 flex flex-wrap justify-center gap-6">
                    <button
                        onClick={() => setSelectedCategory(null)}
                        className={`flex items-center gap-3 px-6 py-2.5 border rounded-full transition-all duration-300 ${
                            !selectedCategory ? 'bg-white text-black border-white' : 'bg-[#0a0a0a] border-rock text-gray-500 hover:border-gray-500'
                        }`}
                    >
                        <span className="text-[10px] font-bold uppercase tracking-widest">
                            {t.cat_all || (lang === 'en' ? 'All' : 'Todos')}
                        </span>
                    </button>
                    {Object.entries(categoryColors).map(([cat, color]) => (
                        <button
                            key={cat}
                            onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                            className={`flex items-center gap-3 px-6 py-2.5 border rounded-full transition-all duration-300 ${
                                selectedCategory === cat
                                    ? 'bg-[#1a1a1a] border-[#00d4ff] text-white shadow-[0_0_15px_rgba(0,212,255,0.2)]'
                                    : (selectedCategory ? 'opacity-40 grayscale border-rock text-gray-600' : 'bg-[#0a0a0a] border-rock text-gray-300 hover:border-gray-500')
                            }`}
                        >
                            <span className="w-2.5 h-2.5 rounded-full shadow-[0_0_10px_rgba(255,255,255,0.2)]" style={{ backgroundColor: color }}></span>
                            <span className="text-[10px] font-bold uppercase tracking-widest">{cat}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};
export default AysenMap;

